
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import RecipeCard from '@/components/RecipeCard';
import CompactRecipeCard from './CompactRecipeCard';
import { UnifiedContent } from '@/types/content';
import { Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface UnifiedContentGridProps {
  content: UnifiedContent[];
  onCompositionEdit?: (composition: any) => void;
  onCompositionDelete?: (composition: any) => void;
  onTagSelect?: (tag: string) => void;
  isMobile?: boolean;
}

const UnifiedContentGrid: React.FC<UnifiedContentGridProps> = ({
  content,
  onCompositionEdit,
  onCompositionDelete,
  onTagSelect,
  isMobile = false
}) => {
  const navigate = useNavigate();
  
  // Use mobile-optimized grid on mobile (2 columns), desktop layout otherwise
  const gridClasses = isMobile 
    ? "grid grid-cols-2 gap-2" 
    : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4";
  
  return (
    <div className={gridClasses}>
      {content.map((item) => (
        <div key={`${item.type}-${item.id}`}>
          {/* Render recipe cards */}
          {item.type === 'recipe' && item.recipe ? (
            isMobile ? (
              <CompactRecipeCard 
                recipe={item.recipe}
                onTagSelect={onTagSelect}
              />
            ) : (
              <RecipeCard 
                recipe={item.recipe}
                onDelete={() => {}} // Empty function since delete is moved to recipe page
                onTagSelect={onTagSelect}
              />
            )
          ) : item.type === 'composition' && item.composition ? (
            /* Render composition as unified card */
            <Card className="h-full flex flex-col border-golden-light bg-card shadow-sm hover:shadow-md transition-shadow duration-200 hover:border-sage/30">
              <CardHeader className="pb-2">
                <CardTitle className="line-clamp-2 text-base md:text-lg font-playfair text-charcoal">
                  {item.composition.name}
                </CardTitle>
              </CardHeader>
              
              <CardContent className="flex-1">
                <p className="text-sm text-muted-foreground line-clamp-3 font-inter">
                  Collection of {item.composition.recipes.length} {item.composition.recipes.length === 1 ? 'recipe' : 'recipes'}
                </p>
                
                {/* Tags Section */}
                <div className="flex flex-wrap gap-1 mt-2">
                  {/* Composition Type Badge */}
                  <Badge variant="outline" className="text-xs border-terracotta text-terracotta bg-terracotta-light/20">
                    <Users className="h-3 w-3 mr-1" />
                    Composition
                  </Badge>
                  
                  {/* Generated Tags - Show all tags */}
                  {item.composition.tags && item.composition.tags.length > 0 && (
                    <>
                      {item.composition.tags.map((tag, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="text-xs bg-golden-light text-charcoal border-golden cursor-pointer hover:bg-golden hover:text-cream transition-colors"
                          onClick={() => onTagSelect?.(tag)}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </>
                  )}
                </div>
              </CardContent>
              
              <CardFooter className="pt-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full h-9 md:h-8 text-sm font-inter border-sage text-sage hover:bg-sage hover:text-cream" 
                  onClick={() => navigate(`/composition/${item.composition.id}`)}
                >
                  View Details
                </Button>
              </CardFooter>
            </Card>
          ) : null}
        </div>
      ))}
    </div>
  );
};

export default UnifiedContentGrid;
